import Select from 'react-select';
import classnames from 'classnames';
import { FiXCircle } from '@vyaguta/vyaguta-icons/fi';
import React, { useEffect, useMemo, useRef } from 'react';

import Badge from 'components/common/badge';
import { filterAllowedRoles } from 'utils/user';
import { useRolesQuery } from 'hooks/query/useRoles';
import { useUserPermissions } from 'hooks/query/useUserInfo';
import AvatarComponent from 'components/common/avatar/AvatarComponent';
import DebounceFormDropdown from 'components/common/debounceFormDropdown/DebounceFormDropdown';
import { handleError } from 'utils/errorHandler';

import type { BlurLikeEvent, DebounceOptions, FormLikeChangeEvent, PermissionMap, RoleItem } from './types';
import { FormikErrors, FormikTouched } from 'formik';

type RoleSelectOption = {
  value: RoleItem;
  label: string;
};

interface Props {
  setSelectedRolesList: React.Dispatch<React.SetStateAction<RoleItem[]>>;
  selectedRolesList: RoleItem[];
  afterChange?: () => void;
  handleBlur?: (e: BlurLikeEvent) => void;
  touched?: {
    userId?: boolean;
    roles?: boolean | FormikTouched<RoleItem>[];
  };
  errors?: {
    userId?: string;
    roles?: string | string[] | FormikErrors<RoleItem>[];
  };
  isEditable?: boolean;
  onUserSelectChange?: (e: FormLikeChangeEvent) => void;
  onUserBlur?: (e: BlurLikeEvent) => void;
  selectedUser?: string | number;
  employeeDebounceOptions?: DebounceOptions;
}

const CreateEditRole: React.FC<Props> = (props) => {
  const {
    setSelectedRolesList,
    selectedRolesList,
    afterChange,
    handleBlur,
    touched,
    errors,
    isEditable,
    onUserSelectChange,
    onUserBlur,
    selectedUser,
    employeeDebounceOptions,
  } = props;

  const permissions: PermissionMap = useUserPermissions();
  const {
    data: roles = [],
    isLoading: isLoadingRoles,
    isError: isRolesError,
    error: rolesError,
    errorUpdatedAt: rolesErrorUpdatedAt,
  } = useRolesQuery();
  const lastHandledRolesErrorAtRef = useRef(0);
  const roleList = useMemo(() => {
    if (isLoadingRoles) {
      return roles;
    }

    return filterAllowedRoles(roles, permissions);
  }, [isLoadingRoles, permissions, roles]);

  const normalizedRoleList = useMemo(() => {
    return roleList.filter((role): role is RoleItem => typeof role.name === 'string' && role.name.trim().length > 0);
  }, [roleList]);

  const roleOptions = useMemo<Array<RoleSelectOption>>(() => {
    const selectedRolesIds = selectedRolesList.reduce<Record<number, number>>((prev, curr) => {
      prev[curr.id] = curr.id;
      return prev;
    }, {});

    return normalizedRoleList.reduce<Array<RoleSelectOption>>((prev, curr) => {
      if (curr.id === selectedRolesIds[curr.id]) {
        return prev;
      }

      prev.push({
        value: { id: curr.id, name: curr.name },
        label: curr.name,
      });

      return prev;
    }, []);
  }, [normalizedRoleList, selectedRolesList]);

  const afterChangeRef = useRef<Props['afterChange']>(afterChange);

  useEffect(() => {
    afterChangeRef.current = afterChange;
  }, [afterChange]);

  useEffect(() => {
    afterChangeRef.current?.();
  }, [selectedRolesList]);

  useEffect(() => {
    if (!isRolesError || !rolesError || rolesErrorUpdatedAt <= lastHandledRolesErrorAtRef.current) {
      return;
    }

    handleError(rolesError);
    lastHandledRolesErrorAtRef.current = rolesErrorUpdatedAt;
  }, [isRolesError, rolesError, rolesErrorUpdatedAt]);

  const handleRoleChange = (option: RoleSelectOption | null) => {
    if (!option) {
      return;
    }

    setSelectedRolesList((state) => [...state, option.value]);
  };

  const handleRemoveRoles = (roleToRemove: RoleItem) => {
    setSelectedRolesList((roles) => roles.filter((role) => role.id !== roleToRemove.id));
  };

  return (
    <>
      {!isEditable && employeeDebounceOptions && (
        <div className="lf-input lf-input--required pb-1rem ">
          <label htmlFor="User's name" className="lf-input__label">
            Name
          </label>
          <div className="custom-dropdown w-80 ">
            <DebounceFormDropdown
              name="employees"
              debounceOptions={employeeDebounceOptions}
              type="employee"
              value={selectedUser}
              error={touched?.userId ? errors?.userId : undefined}
              handleChange={(e) => onUserSelectChange?.(e)}
              handleBlur={(e) => onUserBlur?.(e)}
              className={classnames({ 'is-invalid': touched?.userId && errors?.userId })}
              iconComponent={(option) => (
                <AvatarComponent label={String(option.label ?? '')} value={option as { empId?: string | number }} />
              )}
              isClearable={true}
            />
          </div>
        </div>
      )}

      <div className="lf-input lf-input--required">
        <label htmlFor="User's name" className="lf-input__label">
          Role
        </label>
        <div className="custom-dropdown w-80">
          <Select<RoleSelectOption, false>
            options={roleOptions}
            isLoading={isLoadingRoles}
            isSearchable={true}
            isClearable={false}
            classNamePrefix="lf-select"
            placeholder="Select Roles"
            value={null}
            maxMenuHeight={120}
            onChange={handleRoleChange}
            onBlur={handleBlur}
            className={touched?.roles && errors?.roles ? 'is-invalid' : ''}
          />
        </div>

        <div className={classnames('badge-wrap', { 'mt-3x': selectedRolesList.length !== 0 })}>
          {selectedRolesList.map((role) => (
            <Badge
              key={role.id}
              closeIcon={
                <FiXCircle
                  className="grey--text"
                  size={18}
                  onClick={() => {
                    handleRemoveRoles(role);
                  }}
                />
              }
              label={role.name}
              color={'color-grey-60'}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default CreateEditRole;
